import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { getFeaturesApi, FeaturesResponse } from "src/common/api/features.api";

// Cache key is versioned. Bump this whenever the cached payload shape changes
// or when we need to force-evict any "poisoned" caches written by older code.
export const FEATURE_FLAGS_CACHE_KEY = "ee_features_v2";

// Maximum time we'll block consumers waiting on /features before we flip
// `ready` to true with whatever state we currently hold (defaults to no
// gated features enabled).
const READY_TIMEOUT_MS = 2000;

export type FeatureFlagsContextType = {
    enabledFeatures: string[];
    /**
     * True once /features has either resolved (success or degraded) OR the
     * READY_TIMEOUT_MS deadline has elapsed. Consumers should gate any
     * synchronous flag-dependent actions on this.
     */
    ready: boolean;
    /** True if the most recent /features call returned degraded or failed. */
    degraded: boolean;
    /** True once we've populated state from either the API or the cache. */
    loaded: boolean;
};

const defaultValue: FeatureFlagsContextType = {
    enabledFeatures: [],
    ready: false,
    degraded: false,
    loaded: false,
};

const featureFlagsContext = createContext<FeatureFlagsContextType>(defaultValue);

export const useFeatureFlagsContext = () => useContext(featureFlagsContext);

export const FeatureFlagsProvider = (props: { children: React.ReactNode }) => {
    const [data, setData] = useState<FeatureFlagsContextType>(() => {
        try {
            const cached = sessionStorage.getItem(FEATURE_FLAGS_CACHE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                // Don't trust a previously-cached degraded payload — those
                // poison loads until a successful fetch overwrites them.
                if (!parsed.degraded) {
                    return {
                        enabledFeatures: parsed.enabledFeatures || [],
                        ready: true,
                        degraded: false,
                        loaded: true,
                    };
                }
                sessionStorage.removeItem(FEATURE_FLAGS_CACHE_KEY);
            }
        } catch {
            try { sessionStorage.removeItem(FEATURE_FLAGS_CACHE_KEY); } catch { /* storage unavailable */ }
        }
        return defaultValue;
    });

    // Track readiness across renders without re-running the effect.
    const readyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        let retryCount = 0;
        const maxRetries = 5;

        // Hard deadline: even if /features never resolves, callers shouldn't
        // be blocked forever. After READY_TIMEOUT_MS we flip ready=true with
        // whatever we have (typically defaultValue → no gated features).
        readyTimerRef.current = setTimeout(() => {
            /* istanbul ignore next -- unmount race guard */
            if (!isMountedRef.current) return;
            setData((prev) => (prev.ready ? prev : { ...prev, ready: true }));
        }, READY_TIMEOUT_MS);

        const markReady = () => {
            if (readyTimerRef.current) {
                clearTimeout(readyTimerRef.current);
                readyTimerRef.current = null;
            }
        };

        const fetchFeatures = async () => {
            /* istanbul ignore next -- unmount race guard */
            if (!isMountedRef.current) return;
            try {
                const response: FeaturesResponse = await getFeaturesApi();
                /* istanbul ignore next -- unmount race guard */
                if (!isMountedRef.current) return;
                const newData: FeatureFlagsContextType = {
                    enabledFeatures: response.enabledFeatures || [],
                    ready: true,
                    degraded: response.degraded || false,
                    loaded: true,
                };
                setData(newData);
                markReady();
                try {
                    if (!newData.degraded) {
                        sessionStorage.setItem(
                            FEATURE_FLAGS_CACHE_KEY,
                            JSON.stringify({
                                enabledFeatures: newData.enabledFeatures,
                                degraded: false,
                            })
                        );
                    } else {
                        sessionStorage.removeItem(FEATURE_FLAGS_CACHE_KEY);
                    }
                } catch {
                    // Storage unavailable or quota exceeded -- non-fatal
                }
            } catch (e) {
                /* istanbul ignore next -- unmount race guard */
                if (!isMountedRef.current) return;
                console.error("Failed to fetch feature flags", e);
                try {
                    sessionStorage.removeItem(FEATURE_FLAGS_CACHE_KEY);
                } catch {
                    // Storage unavailable -- non-fatal
                }
                if (retryCount < maxRetries) {
                    retryCount++;
                    setTimeout(fetchFeatures, 3000);
                } else {
                    // Out of retries — flip ready so consumers stop waiting.
                    setData((prev) => ({ ...prev, ready: true, degraded: true, loaded: true }));
                    markReady();
                }
            }
        };
        fetchFeatures();

        return () => {
            isMountedRef.current = false;
            if (readyTimerRef.current) {
                clearTimeout(readyTimerRef.current);
                readyTimerRef.current = null;
            }
        };
    }, []);

    return (
        <featureFlagsContext.Provider value={data}>
            {props.children}
        </featureFlagsContext.Provider>
    );
};
